/**
 * Validates redirect URLs to prevent open redirect attacks.
 * Only allows same-origin paths and whitelisted external domains.
 */

const ALLOWED_DOMAINS = [
  'ofself.ai',
  'app.ofself.ai',
  'api.ofself.ai',
  'www.ofself.ai',
  // .com is the primary domain; .ai is retained and redirects here.
  'ofself.com',
  'nucleus.ofself.com',
  'app.ofself.com',
  'api.ofself.com',
  'www.ofself.com',
];

function isAllowedDomain(hostname) {
  // Allow localhost for development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true;
  }
  return ALLOWED_DOMAINS.some(
    (d) => hostname === d || hostname.endsWith('.' + d)
  );
}

/**
 * Returns true if the URL is safe to redirect to.
 * Safe means: same-origin relative path, or an allowed external domain.
 */
export function isSafeRedirectUrl(url) {
  if (!url || typeof url !== 'string') return false;

  // A browser reads `\` as `/` and strips tabs and newlines, so `/\evil.com`
  // and `/\t/evil.com` are `//evil.com` — another host — though they start with
  // a single slash. No redirect target needs either.
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(url)) return false;

  // Relative paths are always safe
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    // Must be http or https
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }
    return isAllowedDomain(parsed.hostname);
  } catch {
    return false;
  }
}
